import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import { fetchVentures } from "../../../store/slices/venturesSlice";
import Card from "../../../components/base-components/Card";
import Loader from "../../../components/base-components/Loader";
import ErrorMessage from "../../../components/base-components/Error";
import NoData from "../../../components/base-components/NoData";

const VenturesGrid: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { ventures, status, error } = useSelector(
    (state: RootState) => state.ventures
  );

  const baseUrl =
    process.env.REACT_APP_STRAPI_IMAGES_URL?.replace(/\/$/, "") || "";

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchVentures());
    }
  }, [dispatch, status]);

  if (status === "loading") {
    return <Loader />;
  }

  if (status === "failed") {
    return <ErrorMessage message={error} />;
  }

  if (!ventures || ventures.length === 0) {
    return <NoData message="No ventures found" />;
  }

  const totalPages = Math.ceil(ventures.length / itemsPerPage);
  const paginatedVentures = ventures.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <section className="mb-[7rem]">
      <div className="global-container h-full flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 h-full">
          {paginatedVentures.map((venture, index) => (
            <Card
              id={venture.id}
              key={venture.id}
              image={
                venture.attributes.logo.data?.attributes.formats.small?.url
                  ? `${baseUrl}${venture.attributes.logo.data?.attributes.formats.small?.url}`
                  : `${baseUrl}${venture.attributes.logo.data?.attributes.url}`
              }
              title={venture.attributes.title}
              description={
                venture.attributes.shortDescription ||
                "Description not available."
              }
              tags={venture.attributes.tags}
              flipped={index % 2 === 1}
            />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-between items-center gap-4 mt-[2rem]">
            <p className="text-black">
              Page {currentPage} of {totalPages}
            </p>

            <div className="flex">
              {currentPage !== 1 && (
                <button
                  className="mr-[1.2rem]"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                  >
                    <rect
                      x="40"
                      y="40"
                      width="40"
                      height="40"
                      rx="5"
                      transform="rotate(-180 40 40)"
                      fill="white"
                    />
                    <path
                      d="M25 10L15 20L25 30"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
              {currentPage !== totalPages && (
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                  >
                    <rect width="40" height="40" rx="5" fill="white" />
                    <path
                      d="M15 30L25 20L15 10"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default VenturesGrid;
